import { useForm, useWatch } from 'react-hook-form';

import { UsageLineItemTypes } from 'common/src/types/enums/LineItemType.enum';
import {
  ILineItem,
  ILineItemSubtype,
  IMeter,
} from 'common/src/types/interfaces';

import { FormContainer } from 'portal/src/components/common/forms/FormContainer';
import GenericDialog from 'portal/src/components/common/GenericDialog';

import {
  defaultValues,
  getFields,
  LineItemFormFields,
  mapUnitOfMeasureValues,
} from './LineItemForm.fields';
import MeterReadingForm from './MeterReadingForm';

import { useBillFormContext } from '../BillFormContext';

interface ComponentProps {
  defaultEndDate?: Date;
  defaultStartDate?: Date;
  entity?: Partial<ILineItem>;
  meters: Partial<IMeter>[];
  lineItemSubtypes: ILineItemSubtype[];
  lineItemsPreviousBill: Partial<ILineItem>[];
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
}

const AddLineItems = ({
  defaultEndDate,
  defaultStartDate,
  entity = {},
  lineItemSubtypes,
  lineItemsPreviousBill,
  meters,
  open,
  onClose,
  onSave,
}: ComponentProps) => {
  const { billIsUsedInAllocations } = useBillFormContext();
  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    formState: { isDirty },
  } = useForm<LineItemFormFields>({
    defaultValues: defaultValues(
      entity,
      defaultEndDate,
      defaultStartDate,
      meters,
    ),
  });

  const [meterId, type] = useWatch({
    name: ['meterId', 'type'],
    control,
  });
  const isUsage = UsageLineItemTypes.includes(type);

  const handleMeterChange = (newMeterId: string) => {
    const meter = meters.find(({ id }) => id === newMeterId);
    const unitOfMeasure = getValues('unitOfMeasure');
    const unitOptions = mapUnitOfMeasureValues(
      { meterId: newMeterId, type },
      meters,
    );
    const meterPreviousReading = getValues('meterPreviousReading');
    const meterReadingMultiplier = getValues('meterReadingMultiplier');

    if (!unitOptions.some(({ value }) => value === unitOfMeasure)) {
      setValue('unitOfMeasure', null, { shouldDirty: true });
    }

    if (meterPreviousReading === null) {
      setValue(
        'meterPreviousReading',
        lineItemsPreviousBill.find(
          ({ meterId: previousMeterId }) => previousMeterId === newMeterId,
        )?.meterCurrentReading,
      );
    }

    if (meterReadingMultiplier === null) {
      setValue('meterReadingMultiplier', meter?.multiplier);
    }

    setValue('facilityName', meter?.parentFacility?.name);
  };

  const submitForm = (data: LineItemFormFields) => {
    onSave(data);
  };

  const formFieldsConfig = getFields(
    lineItemSubtypes,
    meters,
    meterId,
    type,
    handleMeterChange,
  );

  const setCalculatedUsage = (usage: number) => {
    setValue('usage', usage);
  };

  return (
    <GenericDialog open={open} onClose={onClose}>
      <FormContainer
        backButtonlabel="Cancel"
        buttonLayout="bottom"
        control={control}
        header={`${Object.keys(entity).length ? 'Edit' : 'Add'} Line Item`}
        isDirty={isDirty}
        isNew={!Object.keys(entity).length}
        isPending={false}
        isSubmitDisabled={billIsUsedInAllocations}
        mainFieldsConfig={formFieldsConfig}
        handleBackAction={onClose}
        handleSubmit={handleSubmit(submitForm)}
        removeContainerStyle={true}
        afterMainFields={
          isUsage ? (
            <MeterReadingForm
              setCalculatedUsage={setCalculatedUsage}
              control={control}
              handleSubmit={handleSubmit}
            />
          ) : undefined
        }
      />
    </GenericDialog>
  );
};

export default AddLineItems;
